import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const categories = [
  'Toxina Botulínica',
  'Preenchimento',
  'Lipo de Papada',
  'Skinbooster',
  'Bioestimuladores',
];

const ResultsHero: React.FC = () => {
  const { elementRef, isVisible } = useScrollAnimation({ threshold: 0.2, triggerOnce: false });

  const [activeIndex, setActiveIndex] = useState<number>(0);

  useEffect(() => {
    if (categories.length === 0) return;
    const id = setInterval(() => setActiveIndex((p: number) => (p + 1) % categories.length), 2200);
    return () => clearInterval(id);
  }, [categories.length]);

  const handleScrollToGallery = () => {
    const el = document.getElementById('galeria-cases');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      ref={elementRef as React.RefObject<HTMLElement>}
      className="relative bg-gradient-radial from-[#F5F3EF] via-[#EEE9E2] to-[#E6E1DB] pt-28 md:pt-32 lg:pt-36 pb-14 md:pb-18 lg:pb-20 overflow-hidden"
    >
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-gradient-to-br from-secondary/15 to-transparent blur-3xl animate-float" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-tl from-primary/15 to-transparent blur-3xl animate-float-delay" />
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-secondary/40 to-transparent opacity-70" />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left - Visual composition (invertido para diferenciar) */}
          <div className={`lg:col-span-6 order-1 relative transition-all duration-700 ${isVisible ? 'opacity-100 animate-slide-in-left' : 'opacity-0 -translate-x-6'}`}>
            <div className="relative">
              {/* Main card - ANTES (invertido em relação aos Especialistas) */}
              <div className="relative bg-white p-3 md:p-4 rounded-3xl shadow-2xl transform transition-all duration-700 hover:scale-[1.02] hover:-rotate-1">
                <div className="aspect-[4/3] w-full rounded-2xl overflow-hidden bg-gray-100">
                  <img
                    src="/assets/img/doctor/Drª Brenda .jpg"
                    alt="Drª Brenda"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Floating small card - DEPOIS */}
              <div className="absolute -top-6 -right-6 md:-top-8 md:-right-8 w-36 h-36 md:w-48 md:h-48">
                <div className="bg-white p-2 md:p-3 rounded-2xl shadow-xl h-full transform transition-all duration-500 hover:scale-110 hover:rotate-2 border border-gray-100 animate-float">
                  <div className="rounded-xl overflow-hidden h-full bg-gray-100 relative">
                    <img src="/assets/img/doctor/Arielly .jpg" alt="Arielly" className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>

              {/* Badge */}
              <div className="absolute bottom-4 left-4">
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-secondary" />
                  <span className="text-xs font-semibold text-gray-700">Casos reais</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className={`lg:col-span-6 order-2 transition-all duration-700 ${isVisible ? 'opacity-100 animate-slide-in-right' : 'opacity-0 translate-x-6'}`}> 
            <div className="max-w-xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight">
                <span className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 bg-clip-text text-transparent">Resultados</span>
              </h1>
              <p className="mt-4 text-base md:text-lg text-gray-700 leading-relaxed">
                Casos reais, com abordagem minuciosa e natural. Veja o antes e depois de diferentes procedimentos realizados
                pela nossa equipe.
              </p>

              {/* Chips de categorias com destaque rotativo */}
              <div className="mt-6 flex flex-wrap gap-2">
                {categories.map((c, i) => (
                  <span
                    key={c}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all border ${
                      i === (activeIndex ?? 0)
                        ? 'bg-white text-dark border-secondary ring-2 ring-secondary/60'
                        : 'bg-white/70 text-gray-700 border-gray-200 hover:bg-white'
                    }`}
                  >
                    {c}
                  </span>
                ))}
              </div>

              {/* CTAs (visual invertido para diferenciar) */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleScrollToGallery}
                  className="group inline-flex items-center justify-center gap-3 px-7 py-3 bg-white text-dark font-semibold border-2 border-dark rounded-full hover:border-secondary hover:text-secondary transition-all hover:scale-105"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Ver casos
                </button>

                <Link
                  to="/agendar"
                  className="relative inline-flex items-center justify-center gap-3 px-7 py-3 bg-gradient-to-r from-secondary to-primary text-white font-medium tracking-wide overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105 border-2 border-dark/0 rounded-full"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-primary to-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 relative z-10" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="relative z-10 font-semibold">Agendar avaliação</span>
                </Link>
              </div>

              {/* Métricas rápidas para diferenciar ainda mais */}
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="group bg-white/70 backdrop-blur-sm p-3 rounded-xl border border-gray-200 hover:bg-white hover:shadow-md transition-all">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">120+ casos</p>
                      <p className="text-[11px] text-gray-600">documentados</p>
                    </div>
                  </div>
                </div>
                <div className="group bg-white/70 backdrop-blur-sm p-3 rounded-xl border border-gray-200 hover:bg-white hover:shadow-md transition-all">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M4 6h8a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">Antes/Depois</p>
                      <p className="text-[11px] text-gray-600">com autorização</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-sm text-gray-600 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-secondary/70 animate-pulse" />
                <span>Role para ver nossa galeria de resultados.</span>
              </div>
            </div>
          </div>

          
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-secondary/30 to-transparent" />
    </section>
  );
};

export default ResultsHero;